import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { AthleteTabMenu } from "./AthleteTabMenu";
import AthleteProfileCard from "../component/AthleteProfileCard";
import "chart.js/auto";

const ArrowD = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
    <path d="M13 1L7 7L1 1" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const trainingFocuses = ["Technical", "Speed - Strength", "Power", "Strength - Speed"];

export const AdminAthleteLoadvelocityResult = () => {
  const { staff } = useStaffAuthContext();
  const { athlete_id } = useParams();
  const navigate = useNavigate();

  const [athlete, setAthlete] = useState(null);
  const [performanceData, setPerformanceData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    const fetchPerformance = async () => {
      setLoading(true);
      const data = {};
      try {
        for (const focus of trainingFocuses) {
          try {
            const res = await fetch(`${API_URL.GET_ADMIN_ATHLETE_LOADVELOCITY_PERFORMANCE}${athlete_id}/${focus}`, {
              headers: { Authorization: `Bearer ${staff.token}` },
            });
            if (!res.ok) {
              console.warn(`No data for ${focus}, skipping.`);
              continue; // skip this focus
            }
            const json = await res.json();
            if (json.data) data[focus] = json.data;
          } catch (err) {
            console.warn(`Error fetching ${focus}: ${err.message}, skipping.`);
          }
        }
        setPerformanceData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPerformance();
  }, [athlete_id, staff.token]);
  
  useEffect(() => {
    const fetchPerformance = async () => {
      setLoading(true);
      try {
        const data = {};
        for (const focus of trainingFocuses) {
          const res = await fetch(`${API_URL.GET_ADMIN_ATHLETE_LOADVELOCITY_PERFORMANCE}${athlete_id}/${focus}`, {
            headers: { Authorization: `Bearer ${staff.token}` },
          });
          if (!res.ok) throw new Error(`Failed to fetch ${focus}`);
          const json = await res.json();
          data[focus] = json.data || {}; // backend: { "Week 1": [{ day, speed_data: [...] }, ...], ... }
        }
        setPerformanceData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPerformance();
  }, [athlete_id, staff.token]);

  const toggleAccordion = (index) => setOpenIndex(openIndex === index ? null : index);

  const prepareChartData = (focus) => {
    if (!performanceData[focus]) return { labels: [], datasets: [] };

    const labels = [];
    const speeds = [];

    Object.keys(performanceData[focus]).forEach((weekName) => {
      const weekMatch = weekName.match(/Week\s*(\d+)/i);
      const weekNumber = weekMatch ? weekMatch[1] : "";

      performanceData[focus][weekName].forEach((dayObj) => {
        const shortDay = dayObj.day ? dayObj.day.slice(0, 3) : "";
        const daySpeeds = Array.isArray(dayObj.speed_data) ? dayObj.speed_data : [];
        const maxSpeed = daySpeeds.length > 0 ? Math.max(...daySpeeds) : 0;

        labels.push(`W${weekNumber} - ${shortDay}`);
        speeds.push(maxSpeed);
      });
    });

    return {
      labels,
      datasets: [
        {
          label: `${focus} Max Speed`,
          data: speeds,
          borderColor: "#1E88E5",
          backgroundColor: "rgba(30,136,229,0.15)",
          tension: 0.25,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: { legend: { display: true } },
    scales: {
      x: { ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 } },
      y: { beginAtZero: true, title: { display: true, text: "Speed" } },
    },
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <StaffSideMenu />

      <div className="flex-1 p-6">
        <div className="flex justify-between items-center mb-4">
          {athlete && <AthleteProfileCard athlete={athlete} />}
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
          >
            Go Back
          </button>
        </div>

        <AthleteTabMenu active="loadvelocity-result" />

        <div className="max-w-4xl mx-auto mt-6">
          {trainingFocuses.map((focus, idx) => (
            <div
              key={idx}
              className={`border-b border-gray-300 last:border-none ${
                openIndex === idx ? "bg-gray-50" : ""
              }`}
            >
              <button
                className="w-full text-left text-lg font-medium py-4 flex justify-between items-center"
                onClick={() => toggleAccordion(idx)}
              >
                <span>{focus}</span>
                <span className={openIndex === idx ? "rotate-180" : ""}><ArrowD /></span>
              </button>

              {openIndex === idx && (
                <div className="pb-6">
                  {loading ? <p className="text-gray-500">Loading...</p> :
                    error ? <p className="text-red-500">{error}</p> :
                    <Line data={prepareChartData(focus)} options={chartOptions} />
                  }
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAthleteLoadvelocityResult;
